"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { api } from "@/lib/api";
import { setContractorSession } from "@/lib/auth";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowLeft, 
  ChevronRight, 
  ShieldCheck,
  Briefcase,
  Zap,
  TrendingUp
} from "lucide-react";

export default function ContractorLoginPage() {
  const router = useRouter();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  function update(f: string, v: string) { setForm(p => ({ ...p, [f]: v })); setError(""); }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("registered") === "true") {
      setRegistrationComplete(true);
      const registeredEmail = params.get("email");
      if (registeredEmail) {
        setForm((current) => ({ ...current, email: registeredEmail }));
      }
    }
  }, []);

  async function handleLogin() {
    if (!form.email || !form.password) { setError("Email and password required"); return; }
    setLoading(true);
    try {
      const result = await api.loginContractor({ email: form.email, password: form.password }) as any;
      setContractorSession(result.token, result.contractor.id, result.contractor.name, [result.contractor.trade]);
      router.push("/contractor/dashboard");
    } catch (e: any) {
      setError(e.message || "Invalid credentials for Contractor account");
    } finally { setLoading(false); }
  }

  const reasons = [
    {
      icon: <Briefcase size={24} className="text-emerald-500" />,
      title: "High-Scale Jobs",
      desc: "Access exclusive enterprise contracts and premium residential projects."
    },
    {
      icon: <Zap size={24} className="text-amber-500" />,
      title: "Get Paid Through the Platform",
      desc: "Customers pay securely once they confirm the job is complete."
    },
    {
      icon: <TrendingUp size={24} className="text-blue-500" />,
      title: "Smart Dispatch Engine",
      desc: "Optimize your schedule with AI-powered job matching and routing."
    }
  ];

  return (
    <main className="min-h-screen flex flex-col lg:flex-row font-sans">
      {/* Left Side: Branding & Reasons */}
      <div className="hidden lg:flex lg:w-1/2 bg-white pt-32 px-16 pb-16 flex-col justify-between relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-60 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-blue-100 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-emerald-100 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10">
          <Link href="/" className="brand-logo-shell group">
            <img
              src="/lastest-logo-fixer.png"
              alt="FixerLocal"
              className="brand-logo h-12 select-none transition-transform group-hover:scale-[1.03]"
            />
          </Link>
        </div>

        <div className="relative z-10 max-w-lg">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-bold text-slate-950 leading-tight mb-8"
          >
            Scale your business <br />
            <span className="text-emerald-500">with Fixer Pro.</span>
          </motion.h1>
          
          <div className="space-y-10 mt-16">
            {reasons.map((reason, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 + 0.3 }}
                className="flex gap-6 items-start"
              >
                <div className="h-12 w-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                  {reason.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-950 mb-1">{reason.title}</h3>
                  <p className="text-slate-600 font-medium leading-relaxed">{reason.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-slate-500 text-sm font-bold">
          © 2026 Fixer Technologies. Contractor Division.
        </div>
      </div>

      {/* Right Side: Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-32 pb-12 bg-mesh lg:bg-white">
        <div className="w-full max-w-[420px]">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-12">
             <img
               src="/lastest-logo-fixer.png"
               alt="FixerLocal"
               className="h-14 w-auto select-none"
             />
          </div>

          <div className="mb-10 text-center lg:text-left">
            <h2 className="text-4xl font-bold text-slate-950 tracking-tight mb-3">Contractor Login</h2>
            <p className="text-slate-500 font-medium text-lg">Enter your business credentials to access your job feed.</p>
          </div>

          <div className="space-y-6">
            {registrationComplete && (
              <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm font-bold flex items-center gap-3">
                <ShieldCheck size={18} />
                Contractor account created. Enter your password to continue.
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Business Email</label>
              <div className="relative group">
                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                <input 
                  type="email" 
                  value={form.email} 
                  onChange={e => update("email", e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  placeholder="pro@example.com"
                  className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-200 pl-14 pr-4 text-slate-950 font-medium outline-none focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50 transition-all" 
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <label className="text-sm font-bold text-slate-700">Security Key</label>
              </div>
              <div className="relative group">
                <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={form.password} 
                  onChange={e => update("password", e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  placeholder="••••••••"
                  className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-200 pl-14 pr-14 text-slate-950 font-medium outline-none focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50 transition-all" 
                />
                <button onClick={() => setShowPassword(!showPassword)} className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-600">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm font-bold flex items-center gap-3"
                >
                  <ShieldCheck size={16} fill="currentColor" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <button 
              onClick={handleLogin} 
              disabled={loading}
              className="w-full h-16 rounded-[1.25rem] bg-emerald-600 text-white font-bold text-lg hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-xl shadow-emerald-100 flex items-center justify-center gap-3 active:scale-[0.98] mt-4"
            >
              {loading ? (
                <div className="h-6 w-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Login
                  <ChevronRight size={20} />
                </>
              )}
            </button>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-100 text-center">
            <p className="text-slate-500 font-medium">
              Want to join the network?{" "}
              <Link href="/contractor/onboarding" className="text-emerald-600 font-bold hover:underline underline-offset-4">Register here</Link>
            </p>
          </div>

          <div className="mt-12 flex justify-center items-center gap-6">
            <Link href="/login" className="text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors">Customer Login</Link>
            <div className="h-1 w-1 rounded-full bg-slate-300" />
            <Link href="/admin" className="text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors">Admin Portal</Link>
          </div>
        </div>
      </div>
    </main>
  );
}
